import { forwardRef } from "react";

export type CreditBannerVariant = "low" | "exhausted";

interface CreditBannerProps {
  variant: CreditBannerVariant;
  onTopup: () => void;
}

const COPY: Record<CreditBannerVariant, { title: string; description: string }> = {
  low: {
    title: "Credit balance running low",
    description:
      "You're running low on credits, which may restrict some actions. Top up to avoid interruptions.",
  },
  exhausted: {
    title: "Credits Exhausted",
    description:
      "Your credit balance has been exhausted. Actions that require credits, are currently unavailable.",
  },
};

const STYLES: Record<
  CreditBannerVariant,
  {
    card: string;
    iconColor: string;
    titleColor: string;
    descriptionColor: string;
  }
> = {
  low: {
    card: "bg-[#FFFAEB] border-[#FEC84B]",
    iconColor: "#DC6803",
    titleColor: "text-[#DC6803]",
    descriptionColor: "text-[#344054]",
  },
  exhausted: {
    card: "bg-[#FEF3F2] border-[#FDA29B]",
    iconColor: "#D92D20",
    titleColor: "text-[#D92D20]",
    descriptionColor: "text-[#344054]",
  },
};

/**
 * Persistent credit-status banner pinned directly below the dashboard header.
 *
 * Presentational only: the parent (EmployerDashboardLayout) decides when to show
 * it and which variant to use, measures its height into the `--credit-banner-h`
 * CSS variable (so each page's scroll area can offset by it), and wires the
 * Top up action to the shared top-up modal.
 *
 * Desktop-only (`hidden lg:block`) to match the dashboard header, which is also
 * desktop-only. Mobile renders nothing, so the offset variable stays 0.
 */
const CreditBanner = forwardRef<HTMLDivElement, CreditBannerProps>(
  ({ variant, onTopup }, ref) => {
    const { title, description } = COPY[variant];
    const { card, iconColor, titleColor, descriptionColor } = STYLES[variant];

    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        className="hidden lg:block fixed top-[76px] left-[250px] right-0 z-[990] bg-[#FFFFF] px-[2.2%] pt-[1px] pb-[1px]"
      >
        <div
          className={`flex items-center justify-between gap-6 rounded-[10px] border px-5 py-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] ${card}`}
        >
          <div className="flex min-w-0 items-start gap-3">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke={iconColor}
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mt-[2px] shrink-0"
            >
              <path d="M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86z" />
              <line x1="12" y1="8" x2="12" y2="13" />
              <line x1="12" y1="16.5" x2="12.01" y2="16.5" />
            </svg>
            <div className="min-w-0">
              <h4 className={`font-Gelion font-semibold text-[18px] leading-[26px] ${titleColor}`}>
                {title}
              </h4>
              <p className={`font-Gelion text-[15px] leading-[22px] mt-1 ${descriptionColor}`}>
                {description}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <button
              type="button"
              onClick={onTopup}
              className="h-10 min-w-[132px] rounded-[6px] bg-[#012169] px-4 text-[14px] font-semibold text-white transition-colors hover:bg-[#011557] whitespace-nowrap"
            >
              Topup credit
            </button>
          </div>
        </div>
      </div>
    );
  }
);

CreditBanner.displayName = "CreditBanner";

export { CreditBanner };
